import { addSerialNumberToCsv } from "../lib/csv";
import { formatDateValue } from "../lib/dateFormat";
import DateInput from "../components/DateInput";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const qtyFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

// Validated pair (CVD dE 32.3, normal-vision dE 38.3, contrast >= 3:1).
const C_CY = "#2563eb";
const C_FY = "#d97706";

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function StatTile({ label, value, sub, accent, muted }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        {accent ? (
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: accent }} />
        ) : null}
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          {label}
        </div>
      </div>
      <div className={`mt-2 text-2xl font-semibold ${muted ? "text-slate-400" : "text-slate-900"}`}>
        {value}
      </div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

function PeriodBars({ title, subtitle, rows, colour, max }) {
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: colour }} />
        <div className="text-sm font-semibold text-slate-900">{title}</div>
      </div>
      <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
      <table className="mt-3 min-w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-4">Period</th>
            <th className="py-2 pr-4 text-right">Head-On MCS</th>
            <th className="py-2 pr-4 text-right">Invoices</th>
            <th className="py-2 pr-4 w-32">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.period} className="border-b last:border-0">
              <td className="py-2 pr-4 font-medium text-slate-900">{r.period}</td>
              <td className="py-2 pr-4 text-right">{qtyFmt.format(r.mcs)}</td>
              <td className="py-2 pr-4 text-right text-slate-600">{numFmt.format(r.invoices)}</td>
              <td className="py-2 pr-4">
                <div className="h-2 w-full rounded bg-slate-100">
                  <div className="h-2 rounded"
                       style={{ width: `${max ? (r.mcs / max) * 100 : 0}%`, background: colour }} />
                </div>
              </td>
            </tr>
          ))}
          {!rows.length ? (
            <tr><td className="py-3 text-slate-500" colSpan={4}>No head-on volume in this period.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

export default function HeadOnCyVsFyDetail() {
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [consignee, setConsignee] = useState("");
  const [country, setCountry] = useState("");
  const [form, setForm] = useState("");

  const [options, setOptions] = useState({ consignees: [], countries: [] });
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const body = useMemo(() => ({
    from_date: fromDate || "", to_date: toDate || "",
    consignee: consignee || "", country: country || "", product_form: form || "",
  }), [fromDate, toDate, consignee, country, form]);

  useEffect(() => {
    let active = true;
    api.get("/ceo/filter-options").then((res) => {
      if (!active) return;
      const d = res.data || {};
      setOptions({ consignees: d.consignees || [], countries: d.countries || [] });
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    api.post("/ai/ceo-chat/headon-cy-vs-fy", body, { timeout: 180000 })
      .then((res) => { if (active) setSummary(res.data || null); })
      .catch((e) => {
        if (!active) return;
        setSummary(null);
        setError(e?.response?.data?.detail || e?.message ||
          "Head-on volume could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [body]);

  useEffect(() => {
    let active = true;
    setDetailLoading(true);
    api.post("/ai/ceo-chat/headon-cy-vs-fy/detail", body, { timeout: 180000 })
      .then((res) => { if (active) setDetail(res.data || null); })
      .catch(() => { if (active) setDetail(null); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [body]);

  const rows = useMemo(() => detail?.rows || [], [detail]);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.InvoiceNo, r.Consignee, r.Country, r.Grade, r.FormName]
        .map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(q)));
  }, [rows, search]);

  const exportCsv = useCallback(() => {
    const cols = ["InvoiceNo", "InvoiceDate", "CalendarYear", "FinancialYear",
      "Form", "FormName", "Grade", "Consignee", "Country", "Mcs",
      "TraysQuantity", "Price", "Currency", "QtyUnit"];
    const csv = [cols.map(csvEscape).join(","),
      ...filteredRows.map((r) => cols.map((c) => csvEscape(r?.[c])).join(","))].join("\n");
    const blob = new Blob([addSerialNumberToCsv(csv)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "head-on-volume-cy-vs-fy.csv"; a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows]);

  const s = summary?.summary || {};
  const cyRows = summary?.calendar_year || [];
  const fyRows = summary?.financial_year || [];
  const maxMcs = Math.max(1, ...cyRows.map((r) => r.mcs), ...fyRows.map((r) => r.mcs));
  const valueWithheld = summary && !summary.value_basis?.value_published;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1700px] space-y-5">
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Head-On Volume — Calendar Year vs Financial Year
            </h1>
            <p className="mt-2 max-w-4xl text-sm text-slate-600">
              Total head-on shipped volume, shown two ways: the calendar year
              (January to December) and the financial year (1 April to 31 March).
              Head-on means the product forms Head On Shell On and Head On Body
              Peeled, taken from the ERP product master rather than from
              descriptions.
            </p>
          </div>
          <button type="button"
            onClick={() => navigate("/ceo-command-center?tab=comparisons")}
            className="shrink-0 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Back to Comparisons
          </button>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
        ) : null}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">From (Invoice Date)</span>
              <DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">To (Invoice Date)</span>
              <DateInput value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Product Form</span>
              <select value={form} onChange={(e) => setForm(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Head-On Forms</option>
                <option value="HOSO">Head On Shell On</option>
                <option value="HOBP">Head On Body Peeled</option>
              </select>
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Consignee</span>
              <select value={consignee} onChange={(e) => setConsignee(e.target.value)}
                className="max-w-[240px] rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Consignees</option>
                {options.consignees.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Destination Country</span>
              <select value={country} onChange={(e) => setCountry(e.target.value)}
                className="max-w-[220px] rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Countries</option>
                {options.countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <button type="button"
              onClick={() => { setFromDate(""); setToDate(""); setConsignee(""); setCountry(""); setForm(""); }}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Reset Filters
            </button>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Calendar year = {summary?.date_range?.calendar_year_rule || "January to December"}.
            {" "}Financial year = {summary?.date_range?.financial_year_rule || "1 April to 31 March"}.
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
            Loading head-on volume...
          </div>
        ) : summary ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <StatTile label="Total Head-On Volume" value={qtyFmt.format(s.total_headon_mcs || 0)}
                sub={`MCS across ${numFmt.format(s.total_invoices || 0)} invoice(s)`} />
              <StatTile label={`Calendar Year ${s.current_calendar_year || ""}`}
                value={qtyFmt.format(s.current_calendar_year_mcs || 0)}
                sub="MCS, January to December" accent={C_CY} />
              <StatTile label={`Financial Year ${s.current_financial_year || ""}`}
                value={qtyFmt.format(s.current_financial_year_mcs || 0)}
                sub="MCS, April to March" accent={C_FY} />
              <StatTile label="Head-On Invoice Lines" value={numFmt.format(s.total_lines || 0)}
                sub={`${summary.first_invoice || s.first_invoice || ""} to ${s.last_invoice || ""}`} />
              <StatTile label="Head-On Value" value={valueWithheld ? "Withheld" : qtyFmt.format(s.total_headon_value || 0)}
                muted={valueWithheld}
                sub={valueWithheld ? "Price basis not confirmed — see below" : s.currency} />
            </div>

            {valueWithheld && (summary.value_basis?.reasons_withheld || []).length ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <span className="font-semibold">Why value is not shown:</span>{" "}
                {summary.value_basis.reasons_withheld.join(" ")}{" "}
                Head-on line prices in this data range from{" "}
                {summary.value_basis.price_min} to {summary.value_basis.price_max},
                which indicates more than one price basis. Volume is unaffected and is shown above.
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <PeriodBars title="By Calendar Year" subtitle="January to December"
                rows={cyRows} colour={C_CY} max={maxMcs} />
              <PeriodBars title="By Financial Year" subtitle="1 April to 31 March"
                rows={fyRows} colour={C_FY} max={maxMcs} />
            </div>

            {(summary.product_forms || []).length ? (
              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">Head-On Product Forms</div>
                <table className="mt-3 min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Form</th>
                      <th className="py-2 pr-4">Description</th>
                      <th className="py-2 pr-4 text-right">Lines</th>
                      <th className="py-2 pr-4 text-right">Head-On MCS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.product_forms.map((f) => (
                      <tr key={f.form} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium text-slate-900">{f.form}</td>
                        <td className="py-2 pr-4 text-slate-600">{f.form_name}</td>
                        <td className="py-2 pr-4 text-right">{numFmt.format(f.lines)}</td>
                        <td className="py-2 pr-4 text-right">{qtyFmt.format(f.mcs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Invoice-line Detail</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {detailLoading ? "Loading lines..." : `${numFmt.format(filteredRows.length)} head-on line(s)`}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search invoice, consignee, country, grade"
                    className="w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                  <button type="button" onClick={exportCsv} disabled={!filteredRows.length}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    Export CSV
                  </button>
                </div>
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Invoice No</th>
                      <th className="py-2 pr-4">Invoice Date</th>
                      <th className="py-2 pr-4">Calendar Year</th>
                      <th className="py-2 pr-4">Financial Year</th>
                      <th className="py-2 pr-4">Form</th>
                      <th className="py-2 pr-4">Grade</th>
                      <th className="py-2 pr-4">Consignee</th>
                      <th className="py-2 pr-4">Country</th>
                      <th className="py-2 pr-4 text-right">Head-On MCS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, i) => (
                      <tr key={`${r.InvoiceNo}-${i}`} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-4 font-medium text-slate-900">{r.InvoiceNo}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{formatDateValue(r.InvoiceDate)}</td>
                        <td className="py-2 pr-4">{r.CalendarYear}</td>
                        <td className="py-2 pr-4">{r.FinancialYear}</td>
                        <td className="py-2 pr-4">{r.FormName}</td>
                        <td className="py-2 pr-4">{r.Grade}</td>
                        <td className="py-2 pr-4">{r.Consignee}</td>
                        <td className="py-2 pr-4">{r.Country}</td>
                        <td className="py-2 pr-4 text-right">{qtyFmt.format(r.Mcs)}</td>
                      </tr>
                    ))}
                    {!filteredRows.length && !detailLoading ? (
                      <tr><td className="py-4 text-center text-slate-500" colSpan={9}>
                        No head-on lines match the current filters.
                      </td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {detail?.totals ? (
                <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                  Drill-down totals — {numFmt.format(detail.totals.lines)} line(s) ·{" "}
                  {qtyFmt.format(detail.totals.mcs)} head-on MCS
                </div>
              ) : null}
            </div>

          </>
        ) : null}
      </div>
    </div>
  );
}
